/*
  The descriptor's defaults and the sort engine, as pure functions.

  Component tests do not run in this repo (RTL 13 against React 17, spec §2), so every
  decision a table makes lives here rather than inside a component, and is tested.
*/

import { ReactNode } from "react";

import { createComparator, formatCount, sortRows, SortDirection, SortValue } from "lib";

import { ALIGN_CLASS } from "./tableStyles";
import { ColumnAlign, ColumnCompare, DataTableColumn, TableSortState } from "./types";

/*
  Declaring an accessor is the opt-in; `sortable: false` is the opt-out. A column with
  neither can never render a sort control it cannot honour.
*/
export const isColumnSortable = <TRow>(column: DataTableColumn<TRow>): boolean =>
  column.sortable !== false &&
  (typeof column.sortValue === "function" || typeof column.compare === "function");

export const sortableColumns = <TRow>(
  columns: readonly DataTableColumn<TRow>[]
): DataTableColumn<TRow>[] => columns.filter((column) => isColumnSortable(column));

export const findColumn = <TRow>(
  columns: readonly DataTableColumn<TRow>[],
  id: string
): DataTableColumn<TRow> | null =>
  columns.filter((column) => column.id === id)[0] ?? null;

/* Counts right, everything else left, unless the column says otherwise (§6.2). */
export const columnAlign = <TRow>(column: DataTableColumn<TRow>): ColumnAlign => {
  if (column.align) {
    return column.align;
  }
  return column.numeric === true ? "right" : "left";
};

export const columnAlignClass = <TRow>(column: DataTableColumn<TRow>): string =>
  ALIGN_CLASS[columnAlign(column)];

/*
  What a column with no `render` displays. A numeric column goes through formatCount, so
  "thousands-separated via Intl.NumberFormat" is a property of the primitive rather than
  something each of the five tables has to remember.
*/
export const defaultCellText = <TRow>(
  column: DataTableColumn<TRow>,
  row: TRow
): string => {
  const getValue = column.sortValue;
  if (!getValue) {
    return "";
  }
  const value = getValue(row);
  if (column.numeric === true) {
    return formatCount(typeof value === "number" ? value : Number(value));
  }
  return value === null || value === undefined ? "" : String(value);
};

export const renderCellContent = <TRow>(
  column: DataTableColumn<TRow>,
  row: TRow
): ReactNode => (column.render ? column.render(row) : defaultCellText(column, row));

/*
  §6.5's "visible, rendered columns only". A custom render defaults to "" because JSX
  cannot be matched — a column that renders a link and still wants to be filterable
  declares filterText itself.
*/
export const columnFilterText = <TRow>(
  column: DataTableColumn<TRow>,
  row: TRow
): string => {
  if (column.filterText) {
    return column.filterText(row);
  }
  return column.render ? "" : defaultCellText(column, row);
};

/* The whole row as one haystack, for task 6b's filter input. */
export const rowFilterText = <TRow>(
  columns: readonly DataTableColumn<TRow>[],
  row: TRow
): string =>
  columns
    .map((column) => columnFilterText(column, row))
    .filter((text) => text !== "")
    .join(" ");

/*
  Card numbers are strings, so plain collation orders 1, 10, 100, 2 (§6.4). The comparator
  is built once per direction rather than per comparison, and it comes from
  src/lib/sorting.ts so the empty-last handling is the same one every other column gets.
*/
export const compareByCardNumber = <TRow>(
  getValue: (row: TRow) => SortValue
): ColumnCompare<TRow> => {
  const ascending = createComparator(getValue, "asc", { numeric: true });
  const descending = createComparator(getValue, "desc", { numeric: true });
  return (a, b, direction) =>
    (direction === "desc" ? descending : ascending)(a, b);
};

/*
  Filter -> sort -> paginate is composed by the view, not hidden inside the table, so
  task 6b's filter and pagination cannot end up applied in the wrong order.
*/
export const sortRowsByColumn = <TRow>(
  rows: readonly TRow[],
  columns: readonly DataTableColumn<TRow>[],
  sort: TableSortState
): TRow[] => {
  const column = findColumn(columns, sort.orderBy);
  if (!column || !isColumnSortable(column)) {
    return rows.slice();
  }
  const compare = column.compare;
  if (compare) {
    return rows.slice().sort((a, b) => compare(a, b, sort.order));
  }
  const getValue = column.sortValue;
  if (!getValue) {
    return rows.slice();
  }
  return sortRows(rows, getValue, sort.order);
};

/* A count is most interesting largest-first; a name is most useful A to Z. */
export const defaultSortDirection = <TRow>(
  column: DataTableColumn<TRow>
): SortDirection => (column.numeric === true ? "desc" : "asc");

/*
  What a header click does. Same column toggles direction; a new column adopts its own
  default. An unknown or unsortable id changes nothing.
*/
export const nextSortState = <TRow>(
  columns: readonly DataTableColumn<TRow>[],
  sort: TableSortState,
  columnId: string
): TableSortState => {
  const column = findColumn(columns, columnId);
  if (!column || !isColumnSortable(column)) {
    return sort;
  }
  if (sort.orderBy === columnId) {
    return { orderBy: columnId, order: sort.order === "asc" ? "desc" : "asc" };
  }
  return { orderBy: columnId, order: defaultSortDirection(column) };
};

/* aria-sort's value for one header cell. Undefined on a column that cannot sort. */
export const ariaSortFor = <TRow>(
  column: DataTableColumn<TRow>,
  sort: TableSortState
): "ascending" | "descending" | "none" | undefined => {
  if (!isColumnSortable(column)) {
    return undefined;
  }
  if (sort.orderBy !== column.id) {
    return "none";
  }
  return sort.order === "asc" ? "ascending" : "descending";
};
